import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

/**
 * Modal de confirmación para eliminar una cola. No hace llamadas HTTP: la
 * página decide qué hacer tras onConfirm; si falla, el modal sigue abierto.
 */
export default function ModalEliminarCola({ show, onHide, onConfirm, cola, eliminando = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Dialog
            open={show}
            onClose={eliminando ? undefined : onHide}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: { borderRadius: isMobile ? 0 : 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", m: isMobile ? 0 : 2 },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <WarningAmberIcon sx={{ color: "#cc071e", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: "#212529", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                        {isMobile ? "Eliminar" : "Confirmar eliminación"}
                    </Typography>
                </Box>
                <IconButton onClick={onHide} disabled={eliminando} size="small" aria-label="Cerrar">
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent
                sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, px: { xs: 2, sm: 3 }, flex: isMobile ? 1 : "initial" }}
            >
                <Typography sx={{ fontSize: { xs: 14, sm: 15 }, color: "#495057", lineHeight: 1.6, mb: 2 }}>
                    ¿Estás seguro de que deseas <strong style={{ color: "#cc071e" }}>eliminar</strong> la cola{" "}
                    <strong>{cola?.nombre || "-"}</strong>?
                </Typography>
                <Alert severity="warning">
                    Se eliminarán también todos sus turnos. Esta acción no se puede deshacer.
                </Alert>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: "column-reverse", sm: "row" } }}>
                <Button
                    variant="outlined"
                    onClick={onHide}
                    fullWidth={isMobile}
                    disabled={eliminando}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#6c757d", borderColor: "#6c757d", py: { xs: 1.25, sm: 1 } }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    fullWidth={isMobile}
                    disabled={eliminando}
                    startIcon={eliminando ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <DeleteForeverIcon />}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        py: { xs: 1.25, sm: 1 },
                        backgroundColor: "#cc071e",
                        "&:hover": { backgroundColor: "#a30618" },
                        "&:disabled": { backgroundColor: "#6c757d", color: "#fff" },
                    }}
                >
                    {eliminando ? "Eliminando..." : isMobile ? "Eliminar" : "Eliminar cola"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
